import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wrench, CheckCircle2, Play, Circle, Loader2, FileText, Receipt, ClipboardCheck, Trash2, Plus, MessageSquareText } from "lucide-react";
import { CRM_API, errMsg } from "./api";
import PageHeader from "./components/PageHeader";
import PpfUpgradesPanel from "./components/PpfUpgradesPanel";
import { useCrmAuth } from "./CrmAuth";

const STATUS_META = {
  checked_in: { label: "Checked In", color: "bg-white/15 text-white/80" },
  in_progress: { label: "In Progress", color: "bg-amber-500/25 text-amber-300" },
  quality_control: { label: "Quality Control", color: "bg-pink-500/25 text-pink-300" },
  ready_for_pickup: { label: "Ready for Pickup", color: "bg-emerald-500/25 text-emerald-300" },
  completed: { label: "Completed", color: "bg-emerald-600/30 text-emerald-200" },
  cancelled: { label: "Cancelled", color: "bg-red-500/20 text-red-300" },
};

export function WorkOrdersList() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    CRM_API.get(`/work-orders${status ? `?status=${status}` : ""}`).then(({ data }) => setRows(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div data-testid="crm-wo-list">
      <PageHeader eyebrow="Studio floor" title="Work Orders" subtitle="Every vehicle currently or previously in the shop." />
      <div className="px-6 md:px-10 py-6">
        <div className="mb-5 flex gap-2 flex-wrap">
          <button onClick={() => setStatus("")} className={`px-3 py-1.5 rounded-lg text-xs uppercase font-mono-alt tracking-widest ${status === "" ? "bg-[#1F5EFF] text-white" : "bg-white/5 border border-white/10 text-white/50"}`}>All</button>
          {Object.entries(STATUS_META).map(([k, v]) => (
            <button key={k} onClick={() => setStatus(k)} data-testid={`wo-filter-${k}`}
              className={`px-3 py-1.5 rounded-lg text-xs uppercase font-mono-alt tracking-widest ${status === k ? "bg-[#1F5EFF] text-white" : "bg-white/5 border border-white/10 text-white/50"}`}>{v.label}</button>
          ))}
        </div>
        {loading && <div className="text-white/40 text-sm">Loading…</div>}
        {!loading && rows.length === 0 && <div className="p-12 text-center border border-dashed border-white/10 rounded-xl text-white/40 text-sm">No work orders yet.</div>}
        <div className="space-y-2">
          {rows.map((wo) => (
            <Link key={wo.id} to={`/crm/work-orders/${wo.id}`} data-testid={`wo-${wo.id}`}
              className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#1F5EFF]/40 transition-colors">
              <Wrench className="w-4 h-4 text-[#1F5EFF]" />
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium truncate">{wo.wo_number} · {wo.customer_name || "—"}</div>
                <div className="text-white/40 text-xs truncate">{wo.vehicle_summary} · {wo.license_plate || "—"}</div>
              </div>
              <span className={`px-2.5 py-1 rounded text-[10px] font-mono-alt uppercase tracking-widest ${STATUS_META[wo.status]?.color}`}>
                {STATUS_META[wo.status]?.label || wo.status}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function WorkOrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useCrmAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => CRM_API.get(`/work-orders/${id}`).then(({ data }) => setData(data));
  useEffect(() => { load(); }, [id]);

  const setStatus = async (s) => {
    setBusy(true);
    try {
      const { data: resp } = await CRM_API.patch(`/work-orders/${id}/status`, { status: s });
      if (s === "completed" && resp?.materials_result) {
        const m = resp.materials_result;
        if (m.shortages?.length) toast.warning(`Stock deducted, but short on: ${m.shortages.map((x) => `${x.name} (−${x.short} ${x.unit})`).join(", ")}`);
        else if (m.deducted) toast.success(`Completed — ${m.deducted} material${m.deducted > 1 ? "s" : ""} deducted from stock`);
        else toast.success("Updated");
      } else if (resp?.notify?.ok) {
        toast.success("Updated — customer notified by SMS");
      } else {
        toast.success("Updated");
      }
      await load();
    }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const [qcChecklist, setQcChecklist] = useState(null);
  useEffect(() => { if (data?.work_order?.qc_checklist) setQcChecklist(data.work_order.qc_checklist); }, [data]);

  const submitQc = async () => {
    const allChecked = qcChecklist.every((q) => q.checked);
    if (!allChecked && !confirm("Some QC items are unchecked. Fail QC?")) return;
    setBusy(true);
    try {
      await CRM_API.post(`/work-orders/${id}/qc`, { checklist: qcChecklist, passed: allChecked });
      toast.success(allChecked ? "QC passed · marked ready for pickup" : "QC failed · returned to QC");
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  // Bridge: open the estimate/invoice builder prefilled with this WO's
  // customer, vehicle and assigned services — staff reviews before saving.
  const bridgeTo = (kind) => {
    const services = data.services || [];
    if (services.length === 0) { toast.error("Assign services to this WO first"); return; }
    const svc = services.map((s) => s.id).join(",");
    nav(`/crm/${kind}?new=1&customer=${wo.customer_id}&vehicle=${wo.vehicle_id}&services=${svc}&wo=${id}`);
  };

  const copySms = async () => {
    try {
      const { data } = await CRM_API.get(`/work-orders/${id}/notify-text`);
      await navigator.clipboard.writeText(`To: ${data.phone}\n\n${data.text}`);
      toast.success(`SMS text copied — send it to ${data.phone || "the customer"} from your phone`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="p-10 text-white/40">Loading…</div>;
  const wo = data.work_order;
  const meta = STATUS_META[wo.status] || { label: wo.status, color: "bg-white/10 text-white/80" };

  return (
    <div data-testid="crm-wo-detail">
      <PageHeader
        eyebrow={wo.wo_number}
        title={`${data.customer?.first_name || ""} ${data.customer?.last_name || ""}`.trim() || "Customer"}
        subtitle={[data.vehicle?.year, data.vehicle?.make, data.vehicle?.model].filter(Boolean).join(" ")}
        right={<span className={`px-3 py-1.5 rounded text-[10px] font-mono-alt uppercase tracking-widest ${meta.color}`}>{meta.label}</span>}
      />
      <div className="px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <aside className="space-y-3 text-sm">
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="text-[10px] font-mono-alt text-white/40 uppercase mb-2 tracking-widest">Actions</div>
            <div className="flex flex-col gap-2">
              {wo.status === "checked_in" && <ActionBtn onClick={() => setStatus("in_progress")} icon={Play}>Start work</ActionBtn>}
              {wo.status === "in_progress" && <ActionBtn onClick={() => setStatus("quality_control")} icon={ClipboardCheck}>Move to QC</ActionBtn>}
              {wo.status === "ready_for_pickup" && <ActionBtn onClick={() => setStatus("completed")} icon={CheckCircle2}>Mark completed</ActionBtn>}
              {["in_progress", "ready_for_pickup", "completed"].includes(wo.status) && (
                <ActionBtn onClick={copySms} icon={MessageSquareText}>Copy customer SMS</ActionBtn>
              )}
              <ActionBtn onClick={() => bridgeTo("estimates")} icon={FileText}>Create Estimate</ActionBtn>
              <ActionBtn onClick={() => bridgeTo("invoices")} icon={Receipt}>Create Invoice</ActionBtn>
            </div>
          </div>
          <InfoRow label="Started" value={wo.actual_start ? new Date(wo.actual_start).toLocaleString() : "—"} />
          <InfoRow label="Completed" value={wo.actual_completion ? new Date(wo.actual_completion).toLocaleString() : "—"} />
          <InfoRow label="Bay" value={wo.bay_id ? "assigned" : "—"} />
          <InfoRow label="Inspection" value={wo.inspection_id ? <Link to={`/crm/inspections/${wo.inspection_id}`} className="text-[#8db2ff] hover:underline">View</Link> : "—"} />
        </aside>

        <section className="lg:col-span-2 space-y-6">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-6 h-px bg-[#1F5EFF]" />
              <span className="font-mono-alt text-[10px] tracking-widest text-white/50 uppercase">Services</span>
            </div>
            <div className="space-y-2">
              {(data.services || []).map((s) => (
                <div key={s.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                  <div>
                    <div className="text-white text-sm">{s.name}</div>
                    <div className="text-white/40 text-[10px] font-mono-alt uppercase">{s.category}</div>
                  </div>
                  <div className="font-display text-[#8db2ff] tabular-nums">${s.price}</div>
                </div>
              ))}
              {(!data.services || data.services.length === 0) && <div className="text-white/40 text-sm italic">No services attached.</div>}
            </div>
          </div>

          <MaterialsPanel woId={id} materials={wo.materials || []} closed={["completed", "cancelled"].includes(wo.status)} onChanged={load} />

          {!["completed", "cancelled"].includes(wo.status) && (
            <PpfUpgradesPanel kind="work-orders" docId={id} items={wo.items || []} onChanged={load} />
          )}

          {(wo.status === "quality_control" || wo.status === "in_progress") && qcChecklist && (
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="w-6 h-px bg-[#1F5EFF]" />
                <span className="font-mono-alt text-[10px] tracking-widest text-white/50 uppercase">Quality Control Checklist</span>
              </div>
              <div className="space-y-1">
                {qcChecklist.map((q, i) => (
                  <label key={q.key} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer">
                    <input type="checkbox" checked={q.checked} onChange={(e) => setQcChecklist(qcChecklist.map((x, j) => j === i ? { ...x, checked: e.target.checked } : x))} className="accent-[#1F5EFF] w-4 h-4" data-testid={`qc-${q.key}`} />
                    <span className="text-sm text-white/80 flex-1">{q.label}</span>
                    {q.checked && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                  </label>
                ))}
              </div>
              <button onClick={submitQc} disabled={busy} data-testid="qc-submit"
                className="mt-4 w-full px-5 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium disabled:opacity-40">
                {busy ? <Loader2 className="w-4 h-4 inline animate-spin" /> : "Submit QC"}
              </button>
            </div>
          )}

          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-6 h-px bg-[#1F5EFF]" />
              <span className="font-mono-alt text-[10px] tracking-widest text-white/50 uppercase">Status history</span>
            </div>
            <div className="space-y-1">
              {(wo.status_history || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 text-xs text-white/60">
                  <Circle className="w-2.5 h-2.5 text-[#1F5EFF] fill-[#1F5EFF]" />
                  <span className="capitalize">{h.status.replace(/_/g, " ")}</span>
                  <span className="ml-auto text-white/30 font-mono-alt uppercase">{new Date(h.at).toLocaleString("en-CA")}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function MaterialsPanel({ woId, materials, closed, onChanged }) {
  const [items, setItems] = useState([]);
  const [itemId, setItemId] = useState("");
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    CRM_API.get("/inventory/items").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const add = async () => {
    const q = Number(qty);
    if (!itemId || !q || q <= 0) { toast.error("Pick an item and enter quantity"); return; }
    setBusy(true);
    try {
      await CRM_API.post(`/inventory/work-orders/${woId}/materials`, { item_id: itemId, qty: q });
      toast.success("Material attached — will deduct on completion");
      setItemId(""); setQty("");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const remove = async (mid) => {
    try { await CRM_API.delete(`/inventory/work-orders/${woId}/materials/${mid}`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="wo-materials-panel">
      <div className="flex items-center gap-3 mb-3">
        <span className="w-6 h-px bg-[#1F5EFF]" />
        <span className="font-mono-alt text-[10px] tracking-widest text-white/50 uppercase">Materials used</span>
        <span className="text-white/25 text-[10px]">· auto-deducted from stock on completion</span>
      </div>
      <div className="space-y-2">
        {materials.length === 0 && (
          <div className="text-white/40 text-sm italic">No materials attached yet.</div>
        )}
        {materials.map((m) => (
          <div key={m.id} data-testid={`wo-mat-${m.id}`}
            className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="min-w-0">
              <div className="text-white text-sm truncate">{m.name}</div>
              <div className="text-white/40 text-[10px] font-mono-alt uppercase">
                {m.qty} {m.unit}
                {m.status === "deducted" && m.short_qty ? ` · short ${m.short_qty} ${m.unit}` : ""}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {m.status === "deducted" ? (
                <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono-alt uppercase tracking-wider border ${m.short_qty ? "text-amber-300 bg-amber-500/10 border-amber-500/25" : "text-emerald-300 bg-emerald-500/10 border-emerald-500/25"}`}
                  data-testid={`wo-mat-status-${m.id}`}>
                  {m.short_qty ? "Deducted · short" : "Deducted"}
                </span>
              ) : (
                <>
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-mono-alt uppercase tracking-wider border text-white/50 bg-white/5 border-white/15">Planned</span>
                  {!closed && (
                    <button onClick={() => remove(m.id)} data-testid={`wo-mat-del-${m.id}`}
                      className="p-1.5 rounded text-white/40 hover:text-red-400 hover:bg-red-500/10">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
        {!closed && (
          <div className="flex items-center gap-2 pt-1">
            <select value={itemId} onChange={(e) => setItemId(e.target.value)} data-testid="wo-mat-select"
              className="flex-1 bg-white/5 border border-white/12 rounded-lg px-3 py-2 outline-none focus:border-[#1F5EFF] text-white text-sm">
              <option value="">Select material…</option>
              {items.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.name} · {it.quantity} {it.unit} in stock
                </option>
              ))}
            </select>
            <input type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Qty"
              data-testid="wo-mat-qty"
              className="w-24 bg-white/5 border border-white/12 rounded-lg px-3 py-2 outline-none focus:border-[#1F5EFF] text-white text-sm" />
            <button onClick={add} disabled={busy} data-testid="wo-mat-add"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1F5EFF]/15 hover:bg-[#1F5EFF] border border-[#1F5EFF]/30 text-[#8db2ff] hover:text-white text-sm font-medium transition-colors disabled:opacity-40">
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const ActionBtn = ({ onClick, icon: Icon, children }) => (  <button onClick={onClick} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1F5EFF]/15 hover:bg-[#1F5EFF] hover:text-white border border-[#1F5EFF]/30 text-[#8db2ff] text-sm font-medium transition-colors">
    <Icon className="w-4 h-4" /> {children}
  </button>
);

const InfoRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-white/5 border border-white/8">
    <div className="text-[11px] font-mono-alt text-white/40 uppercase tracking-widest">{label}</div>
    <div className="text-white text-sm text-right">{value}</div>
  </div>
);
